import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import PopupModelWrapper from "../wrappers/PopupModelWrapper";
import {
    IconButton,
    MenuItem,
    Select,
    Stack,
    TableCell,
    TableRow,
    Typography,
} from "@mui/material";
import PaginationTable from "../table/PaginationTable";
import { HeadColType } from "src/types/paginationTypes";
import { BsTrash } from "react-icons/bs";
import PopupButton from "../common/PopupButton";
import EmptyTable from "../table/EmptyTable";
import useData from "src/hooks/useApi";
import useMessage from "../../hooks/useMessage";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Grid from "@mui/material/Grid";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { Order, OrderResponse } from "./BackorderModel";
import { deleteIcon, prioritySelector, priorityTableRow } from "./modelStyles";
import PopupLoading from "../common/PopupLoading";
import psl from "psl";

type ExpandedOrder = {
    domainName: string;
    priority: number;
    successFee: number;
    registerFee: number;
};

type PrioritySelectType = {
    isOpen: boolean;
    domains: Order[];
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    setIsResponseOpen: Dispatch<SetStateAction<boolean>>;
    setOrderResponse: Dispatch<SetStateAction<OrderResponse[]>>;
};

const priorityLevels = [1, 2, 3, 4, 5];

type PriorityDropDown = {
    priority: number;
    setPriority: (e: any) => void;
    text?: string;
};

export type PriceResponse = {
    tld: string;
    successFee: string;
    regFee: string;
};

const SelectPriorityModel = (props: PrioritySelectType) => {
    const { isOpen, domains, setIsOpen, setIsResponseOpen, setOrderResponse } =
        props;

    const { postData } = useData();
    const { showError } = useMessage();

    const [expandedDomains, setExpandedDomains] = useState<ExpandedOrder[]>([]);
    const [globalPriority, setGlobalPriority] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [page, setPage] = useState<number>(0);
    const [rowsPerPage] = useState<number>(4);

    const [isSubmitAllowed, setSubmitAllowed] = useState<boolean>(true);

    const visibleDomains = useMemo(() => {
        return expandedDomains.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage,
        );
    }, [expandedDomains, page, rowsPerPage]);

    const changeGlobalPriority = (e: any) => {
        const value = e.target.value ? +e.target.value : 1;
        setGlobalPriority(value);
        setExpandedDomains((prevExpandedDomains) =>
            prevExpandedDomains.map((domain) => ({
                ...domain,
                priority: value,
            })),
        );
    };

    const changeSinglePriority = (e: any, domainName: string) => {
        const value = e.target.value ? +e.target.value : 1;
        setExpandedDomains((prevExpandedDomains) =>
            prevExpandedDomains.map((domain) =>
                domain.domainName === domainName
                    ? {
                          ...domain,
                          priority: value,
                      }
                    : domain,
            ),
        );
    };

    const removeDomain = (domainName: string) => {
        setExpandedDomains((prevExpandedDomains) => {
            const updatedDomains = prevExpandedDomains.filter(
                (domain) => domain.domainName !== domainName,
            );

            if (updatedDomains.length === 0) {
                handleClose();
            }

            return updatedDomains;
        });
    };

    const executePlaceBids = async (backorderPayload: ExpandedOrder[]) => {
        try {
            const bids: Order[] = backorderPayload.map((domain) => ({
                domainName: domain.domainName,
                priority: domain.priority,
            }));

            const response: any = await postData("bid/placeBids", {
                bids,
            });

            if (!response.status) {
                showError(response.error);
                return;
            }

            const tempOrderResponse = response.data.map((item: any) => ({
                domain: item.domain,
                success: item.success,
                reason: item.message,
            }));

            setOrderResponse(tempOrderResponse);
            setIsResponseOpen(true);
        } catch (error: any) {
            showError("Some Error Occurred, Please Try Again Later !");
        } finally {
            handleClose();
        }
    };

    const checkBalance = async (backorderPayload: ExpandedOrder[]) => {
        try {
            const pendingBids = backorderPayload.map((domain) => ({
                domain: domain.domainName,
                priority: domain.priority,
                price: Number(
                    (
                        domain.registerFee +
                        domain.successFee * domain.priority
                    ).toFixed(2),
                ),
            }));

            const response: any = await postData("bid/checkBalance", {
                pendingBids,
            });

            if (!response.status) {
                showError("Balance Check Failed");
                return false;
            }

            if (response.isBalanceSufficient) {
                return true;
            }

            showError(
                `Balance not sufficient, need minimum of ${response.requiredBalance} USD`,
            );

            return false;
        } catch (error: any) {
            showError("Some Error Occurred, Please Try Again Later !");
        }
    };

    const fetchFeesAndSetGPriority = async (domainsToExpand: Order[]) => {
        try {
            setIsLoading(true);

            const tldSet = new Set<string>();
            const domainsWithTld = [];

            for (const domain of domainsToExpand) {
                const parsedDomain = psl.parse(domain.domainName.trim());

                if (parsedDomain && "tld" in parsedDomain && parsedDomain.tld) {
                    const tld = parsedDomain.tld;
                    tldSet.add(tld);
                    domainsWithTld.push({ ...domain, tld: tld });
                }
            }

            const tldList = Array.from(tldSet);

            if (tldList.length === 0) {
                showError("TLDs Provided are not valid !");
                handleClose();
                return;
            }

            const response: any = await postData("/tld/getPrices", {
                tlds: Array.from(tldList),
            });

            if (!response || !response.success) {
                showError("Error Occurred when fetching TLD Info");
                handleClose();
                return;
            }

            const priceMap = new Map<string, PriceResponse>(
                response.data.map((item: PriceResponse) => [item.tld, item]),
            );

            const tempDomains: ExpandedOrder[] = [];
            let minPriority = 5;

            for (const domain of domainsWithTld) {
                const priceInfo = priceMap.get(domain.tld);
                if (priceInfo) {
                    tempDomains.push({
                        domainName: domain.domainName,
                        priority: domain.priority,
                        successFee: parseFloat(priceInfo.successFee),
                        registerFee: parseFloat(priceInfo.regFee),
                    });

                    if (domain.priority < minPriority) {
                        minPriority = domain.priority;
                    }
                }
            }

            if (tempDomains.length === 0) {
                showError("TLDs provided are not supported");
                handleClose();
                return;
            }

            if (domainsToExpand.length > tempDomains.length) {
                showError("Some backorders with unsupported TLDs were removed");
            }

            setGlobalPriority(minPriority);
            setExpandedDomains(tempDomains);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        setGlobalPriority(1);
    };

    const handleSubmitClick = async () => {
        const isBalanceSufficient = await checkBalance(expandedDomains);

        if (isBalanceSufficient) {
            await executePlaceBids(expandedDomains);
        } else {
            setSubmitAllowed(false);
        }
    };

    useEffect(() => {
        fetchFeesAndSetGPriority(domains);
    }, [domains]);

    useEffect(() => {
        setSubmitAllowed((prevState) => (!prevState ? true : prevState));
    }, [expandedDomains]);

    const headRow: HeadColType[] = [
        {
            label: "Domain",
            align: "center",
        },
        {
            label: "priority",
            align: "center",
            component: (
                <Grid container display={"flex"} flexDirection={"row"}>
                    <PrioritySelector
                        setPriority={changeGlobalPriority}
                        priority={globalPriority}
                        text="Priority"
                    />
                    <Tooltip
                        title={
                            <Typography
                                sx={{ color: "#605C66", fontSize: "10px" }}
                            >
                                {
                                    "You can select the priority for the bid that you are adding. If your priority is the highest, the domain is yours. If there are multiple bidders with the same highest priority, the system will create an auction."
                                }
                            </Typography>
                        }
                        arrow
                        enterTouchDelay={0}
                        componentsProps={{
                            popper: {
                                sx: {
                                    [`& .${tooltipClasses.arrow}`]: {
                                        color: "#EFD016",
                                    },
                                    [`& .${tooltipClasses.tooltip}`]: {
                                        backgroundColor: "#EFD016",
                                        borderRadius: 3,
                                    },
                                },
                            },
                        }}
                    >
                        <HelpOutlineIcon
                            style={{ color: "#605C66", fontSize: "1.4em" }}
                        />
                    </Tooltip>
                </Grid>
            ),
        },
        {
            label: "Bid ($)",
            align: "right",
        },
        {
            label: "Action",
            align: "center",
        },
    ];

    if (isLoading) {
        return (
            <PopupModelWrapper
                open={isOpen}
                handleClose={handleClose}
                width={{ xs: "80%", sm: "300px", md: "650px" }}
                title="Select Priority"
            >
                <PopupLoading />
            </PopupModelWrapper>
        );
    }

    return (
        <PopupModelWrapper
            open={isOpen}
            handleClose={handleClose}
            width={{ xs: "80%", sm: "300px", md: "650px" }}
            title="Select Priority"
        >
            <Stack sx={{ my: 3 }}>
                <PaginationTable
                    rowsCount={expandedDomains.length}
                    rowsPerPage={rowsPerPage}
                    headRow={headRow}
                    page={page + 1}
                    setPage={setPage}
                >
                    {visibleDomains.length > 0 ? (
                        visibleDomains.map(
                            (row: ExpandedOrder, index: number) => (
                                <TableRow
                                    key={`priority_row_${index}_${row.domainName}`}
                                    sx={priorityTableRow}
                                >
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                    >
                                        {row.domainName}
                                    </TableCell>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="right"
                                    >
                                        <PrioritySelector
                                            priority={row.priority}
                                            setPriority={(e) => {
                                                changeSinglePriority(
                                                    e,
                                                    row.domainName,
                                                );
                                            }}
                                            text=""
                                        />
                                    </TableCell>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="right"
                                    >
                                        {(
                                            row.registerFee +
                                            row.successFee * row.priority
                                        ).toFixed(2)}
                                    </TableCell>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                    >
                                        <IconButton
                                            onClick={() =>
                                                removeDomain(row.domainName)
                                            }
                                        >
                                            <BsTrash style={deleteIcon} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ),
                        )
                    ) : (
                        <EmptyTable colSpan={4} msg="No Data" />
                    )}
                </PaginationTable>
            </Stack>
            <Stack direction="row" justifyContent="flex-end">
                <PopupButton triggerFunction={handleClose} type="cancel">
                    Cancel
                </PopupButton>
                <PopupButton
                    triggerFunction={handleSubmitClick}
                    type="proceed"
                    disabled={expandedDomains.length === 0 || !isSubmitAllowed}
                >
                    Submit
                </PopupButton>
            </Stack>
        </PopupModelWrapper>
    );
};

const PrioritySelector = (props: PriorityDropDown) => {
    const { priority, setPriority, text } = props;
    return (
        <Stack direction="row" alignItems="center" justifyContent="flex-end">
            {text ? <Typography fontSize="14px">{text}</Typography> : <></>}
            <Select
                id="demo-simple-select"
                value={priority}
                onChange={setPriority}
                sx={prioritySelector}
            >
                {priorityLevels.map((level) => (
                    <MenuItem key={level} value={level}>
                        {level}
                    </MenuItem>
                ))}
            </Select>
        </Stack>
    );
};

export default SelectPriorityModel;
